import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ChevronDown, ChevronRight, Loader2, Wand2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ErrorState } from '@/components/error-state'
import { RepoCombobox, type GitHubRepo } from '@/components/repo-combobox'
import { RefCombobox } from '@/components/ref-combobox'
import { $api } from '@/api/client'
import { describeError } from '@/lib/api-error'
import { cn } from '@/lib/utils'
import type { components } from '@/api/openapi'

type RunCreateBody = components['schemas']['RunCreate']
type InvocationOverride = components['schemas']['InvocationOverride']
type RunOutputInput = components['schemas']['RunOutputInput']
type AgentOverride = 'default' | 'codex' | 'claude'

const REPO_SLUG = /^[A-Za-z0-9._-]+\/[A-Za-z0-9._-]+$/

const BRANCH_REGEX =
  /^(?!-)(?!.*\.\.)(?!.*@\{)(?!.*\.lock(?:\/|$))(?!\/)(?!.*\/$)(?!.*\/\/)[^\s\x00-\x1f~^:?*[\\]+$/

function parseJsonObject(input: string | undefined): Record<string, unknown> | undefined {
  if (!input || !input.trim()) return undefined
  try {
    const parsed: unknown = JSON.parse(input)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>
    }
    return undefined
  } catch {
    return undefined
  }
}

function isValidJsonObjectOrEmpty(input: string | undefined): boolean {
  if (!input || !input.trim()) return true
  try {
    const parsed: unknown = JSON.parse(input)
    return Boolean(parsed) && typeof parsed === 'object' && !Array.isArray(parsed)
  } catch {
    return false
  }
}

function trimmedOrEmpty(s: string | undefined): string {
  return (s ?? '').trim()
}

const formSchema = z.object({
  harness_repo: z
    .string()
    .min(3, 'Required.')
    .regex(REPO_SLUG, 'Use owner/repo format.'),
  workspace_repo: z
    .string()
    .min(3, 'Required.')
    .regex(REPO_SLUG, 'Use owner/repo format.'),
  harness_ref: z.string().min(1).default('main'),
  base_ref: z.string().min(1).default('main'),
  task: z
    .string()
    .min(8, 'Describe the task in at least 8 characters.')
    .max(20_000, 'Task is too long.'),
  timeout_seconds: z
    .number({ message: 'Provide a number.' })
    .int()
    .min(60, 'Minimum 60 seconds.')
    .max(14_400, 'Max 4 hours (14,400 s).')
    .optional(),
  invocation_agent: z.enum(['default', 'codex', 'claude']).default('default'),
  invocation_model: z
    .string()
    .max(200, 'Max 200 characters.')
    .optional()
    .or(z.literal('')),
  invocation_options: z
    .string()
    .max(20_000, 'Too large.')
    .optional()
    .refine(isValidJsonObjectOrEmpty, 'Must be a JSON object (e.g. {"key": "value"}).'),
  output_branch: z
    .string()
    .max(255, 'Branch name too long.')
    .optional()
    .refine(
      (v) => !v || !v.trim() || BRANCH_REGEX.test(v.trim()),
      'Invalid git branch name.',
    ),
  output_commit_title: z
    .string()
    .max(120, 'Max 120 characters.')
    .optional()
    .or(z.literal('')),
  output_commit_body: z
    .string()
    .max(8192, 'Max 8192 characters.')
    .optional()
    .or(z.literal('')),
  metadata: z
    .string()
    .max(20_000, 'Too large.')
    .optional()
    .refine(isValidJsonObjectOrEmpty, 'Must be a JSON object (e.g. {"key": "value"}).'),
  idempotency_key: z
    .string()
    .max(128, 'Max 128 characters.')
    .optional()
    .or(z.literal('')),
})

type FormValues = z.input<typeof formSchema>
type FormOutput = z.output<typeof formSchema>

const DEFAULTS: FormValues = {
  harness_repo: '',
  workspace_repo: '',
  harness_ref: 'main',
  base_ref: 'main',
  task: '',
  timeout_seconds: 1800,
  invocation_agent: 'default',
  invocation_model: '',
  invocation_options: '',
  output_branch: '',
  output_commit_title: '',
  output_commit_body: '',
  metadata: '',
  idempotency_key: '',
}

export type TriggerInitial = Partial<RunCreateBody>

function stringifyJson(value: Record<string, unknown> | undefined): string {
  if (!value) return ''
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return ''
  }
}

function buildDefaults(initial: TriggerInitial | undefined): FormValues {
  if (!initial) return { ...DEFAULTS }
  const inv = initial.invocation
  const out = initial.output
  const invocationAgent: FormValues['invocation_agent'] =
    inv?.agent === 'codex' || inv?.agent === 'claude' ? inv.agent : 'default'
  return {
    ...DEFAULTS,
    ...(initial.harness_repo ? { harness_repo: initial.harness_repo } : {}),
    ...(initial.workspace_repo ? { workspace_repo: initial.workspace_repo } : {}),
    ...(initial.harness_ref ? { harness_ref: initial.harness_ref } : {}),
    ...(initial.base_ref ? { base_ref: initial.base_ref } : {}),
    ...(initial.task ? { task: initial.task } : {}),
    ...(typeof initial.timeout_seconds === 'number'
      ? { timeout_seconds: initial.timeout_seconds }
      : {}),
    invocation_agent: invocationAgent,
    invocation_model: inv?.model ?? '',
    invocation_options: stringifyJson(inv?.options),
    output_branch: out?.branch ?? '',
    output_commit_title: out?.commit_title ?? '',
    output_commit_body: out?.commit_body ?? '',
    metadata: stringifyJson(initial.metadata as Record<string, unknown> | undefined),
  }
}

export function TriggerRunDialog({
  open,
  onOpenChange,
  initial,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  initial?: TriggerInitial | undefined
}) {
  const navigate = useNavigate()
  const create = $api.useMutation('post', '/runs')

  const form = useForm<FormValues, unknown, FormOutput>({
    resolver: zodResolver(formSchema),
    defaultValues: buildDefaults(initial),
    mode: 'onBlur',
  })

  useEffect(() => {
    if (!open) return
    form.reset(buildDefaults(initial))
    create.reset()
    setAdvancedOpen(false)
    // form (react-hook-form) returns a stable object; create (TanStack Query
    // useMutation) returns a fresh result object on every render. Including
    // either in deps would re-fire reset on unrelated re-renders (e.g. when a
    // sibling field's value changes), wiping user input. We intentionally only
    // depend on the open transition and the initial seed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial])

  const harnessRepoValue = form.watch('harness_repo')
  const workspaceRepoValue = form.watch('workspace_repo')

  const onHarnessRepoChange = (next: string) => {
    form.setValue('harness_repo', next, { shouldDirty: true, shouldValidate: true })
  }
  const onWorkspaceRepoChange = (next: string) => {
    form.setValue('workspace_repo', next, { shouldDirty: true, shouldValidate: true })
  }
  const onHarnessRefChange = (next: string) => {
    form.setValue('harness_ref', next, { shouldDirty: true, shouldValidate: true })
  }
  const onBaseRefChange = (next: string) => {
    form.setValue('base_ref', next, { shouldDirty: true, shouldValidate: true })
  }

  const onHarnessRepoSelect = (repo: GitHubRepo) => {
    if (!form.formState.dirtyFields.harness_ref) {
      form.setValue('harness_ref', repo.default_branch, { shouldDirty: false })
    }
  }
  const onWorkspaceRepoSelect = (repo: GitHubRepo) => {
    if (!form.formState.dirtyFields.base_ref) {
      form.setValue('base_ref', repo.default_branch, { shouldDirty: false })
    }
  }

  const submit = form.handleSubmit((values) => {
    const body: RunCreateBody = {
      harness_repo: values.harness_repo,
      workspace_repo: values.workspace_repo,
      base_ref: values.base_ref || 'main',
      harness_ref: values.harness_ref || 'main',
      task: values.task,
      ...(values.timeout_seconds ? { timeout_seconds: values.timeout_seconds } : {}),
    }

    const invocation: InvocationOverride = {}
    if (values.invocation_agent !== 'default') {
      invocation.agent = values.invocation_agent as Exclude<AgentOverride, 'default'>
    }
    if (trimmedOrEmpty(values.invocation_model)) {
      invocation.model = trimmedOrEmpty(values.invocation_model)
    }
    const parsedOptions = parseJsonObject(values.invocation_options)
    if (parsedOptions) invocation.options = parsedOptions
    if (Object.keys(invocation).length > 0) body.invocation = invocation

    const output: RunOutputInput = {}
    if (trimmedOrEmpty(values.output_branch)) {
      output.branch = trimmedOrEmpty(values.output_branch)
    }
    if (trimmedOrEmpty(values.output_commit_title)) {
      output.commit_title = trimmedOrEmpty(values.output_commit_title)
    }
    if ((values.output_commit_body ?? '').length > 0) {
      output.commit_body = values.output_commit_body ?? ''
    }
    if (Object.keys(output).length > 0) body.output = output

    const parsedMetadata = parseJsonObject(values.metadata)
    if (parsedMetadata) body.metadata = parsedMetadata

    create.mutate(
      {
        body,
        ...(trimmedOrEmpty(values.idempotency_key)
          ? {
              params: {
                header: { 'idempotency-key': trimmedOrEmpty(values.idempotency_key) },
              },
            }
          : {}),
      },
      {
        onSuccess: (run) => {
          toast.success('Run queued', {
            description: `Run ${run.run_id.slice(0, 12)}…`,
          })
          onOpenChange(false)
          void navigate({ to: '/runs/$runId', params: { runId: run.run_id } })
        },
        onError: (err) => {
          const desc = describeError(err)
          toast.error(desc.title, { description: desc.message })
        },
      },
    )
  })

  const errors = form.formState.errors
  const isSubmitting = create.isPending || form.formState.isSubmitting
  const [dialogContent, setDialogContent] = useState<HTMLDivElement | null>(null)
  const [advancedOpen, setAdvancedOpen] = useState(false)

  const advancedDirty = useMemo(() => {
    const v = form.watch()
    return Boolean(
      v.timeout_seconds && v.timeout_seconds !== DEFAULTS.timeout_seconds,
    ) ||
      v.invocation_agent !== 'default' ||
      Boolean(trimmedOrEmpty(v.invocation_model)) ||
      Boolean(trimmedOrEmpty(v.invocation_options)) ||
      Boolean(trimmedOrEmpty(v.output_branch)) ||
      Boolean(trimmedOrEmpty(v.output_commit_title)) ||
      Boolean((v.output_commit_body ?? '').length > 0) ||
      Boolean(trimmedOrEmpty(v.metadata)) ||
      Boolean(trimmedOrEmpty(v.idempotency_key))
  }, [form])

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (isSubmitting) return
        onOpenChange(next)
      }}
    >
      <DialogContent
        ref={setDialogContent}
        className="flex flex-col p-0 overflow-hidden gap-0 sm:max-w-[640px] max-h-[90dvh]"
        onInteractOutside={(e) => {
          if (isSubmitting) e.preventDefault()
        }}
        onEscapeKeyDown={(e) => {
          if (isSubmitting) e.preventDefault()
        }}
      >
        <DialogHeader className="border-b border-border/70 px-6 py-4">
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="size-4 text-amber-500" />
            Trigger run
          </DialogTitle>
          <DialogDescription>Enqueue a harness run.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="flex flex-1 min-h-0 flex-col overflow-hidden">
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
            <div className="space-y-5">
              {create.isError ? (
                <ErrorState error={create.error} />
              ) : null}

              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Harness repo"

                  error={errors.harness_repo?.message}
                  required
                >
                  <RepoCombobox
                    value={harnessRepoValue}
                    onChange={onHarnessRepoChange}
                    onSelectRepo={onHarnessRepoSelect}
                    placeholder="Select harness repository…"
                    popoverContainer={dialogContent}
                    harnessOnly
                    aria-invalid={Boolean(errors.harness_repo)}
                  />
                </Field>

                <Field
                  label="Workspace repo"
                  hint="The repository to modify."
                  error={errors.workspace_repo?.message}
                  required
                >
                  <RepoCombobox
                    value={workspaceRepoValue}
                    onChange={onWorkspaceRepoChange}
                    onSelectRepo={onWorkspaceRepoSelect}
                    placeholder="Select workspace repository…"
                    popoverContainer={dialogContent}
                    aria-invalid={Boolean(errors.workspace_repo)}
                  />
                </Field>

                <Field
                  label="Harness ref"

                  error={errors.harness_ref?.message}
                >
                  <RefCombobox
                    repo={harnessRepoValue}
                    value={form.watch('harness_ref') ?? ''}
                    onChange={onHarnessRefChange}
                    popoverContainer={dialogContent}
                    aria-invalid={Boolean(errors.harness_ref)}
                  />
                </Field>

                <Field
                  label="Base ref"

                  error={errors.base_ref?.message}
                >
                  <RefCombobox
                    repo={workspaceRepoValue}
                    value={form.watch('base_ref') ?? ''}
                    onChange={onBaseRefChange}
                    popoverContainer={dialogContent}
                    aria-invalid={Boolean(errors.base_ref)}
                  />
                </Field>
              </div>

              <Field
                label="Task"

                error={errors.task?.message}
                required
              >
                <textarea
                  {...form.register('task')}
                  rows={6}
                  placeholder="Draft clarifying questions for the latest kakao thread."
                  className={cn(
                    'w-full rounded-md border bg-background px-3 py-2 font-mono text-[12.5px] leading-relaxed shadow-xs outline-none',
                    'min-h-[8rem] resize-y',
                    'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
                    errors.task && 'border-destructive ring-destructive/30',
                  )}
                />
              </Field>

              <AdvancedSection
                open={advancedOpen}
                onToggle={() => setAdvancedOpen((v) => !v)}
                dirty={advancedDirty}
              >
                <AdvancedGroup heading="Execution">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field
                      label="Timeout (s)"

                      error={errors.timeout_seconds?.message}
                    >
                      <Input
                        type="number"
                        min={60}
                        max={14_400}
                        {...form.register('timeout_seconds', { valueAsNumber: true })}
                        className="font-mono text-[12.5px]"
                      />
                    </Field>
                    <Field
                      label="Agent CLI override"
                      hint="Use the default from your harness config."
                    >
                      <Select
                        value={form.watch('invocation_agent') ?? 'default'}
                        onValueChange={(v) =>
                          form.setValue(
                            'invocation_agent',
                            v as FormValues['invocation_agent'],
                            { shouldDirty: true },
                          )
                        }
                      >
                        <SelectTrigger className="font-mono text-[12.5px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">manifest default</SelectItem>
                          <SelectItem value="codex">codex</SelectItem>
                          <SelectItem value="claude">claude</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>
                  <Field
                    label="Model override"
                    hint="Leave blank to use your agent's default."
                    error={errors.invocation_model?.message}
                  >
                    <Input
                      {...form.register('invocation_model')}
                      placeholder="gpt-5.4"
                      className="font-mono text-[12.5px]"
                      autoComplete="off"
                    />
                  </Field>
                  <Field
                    label="Invocation options"
                    hint='JSON object (merged with agent defaults).'
                    error={errors.invocation_options?.message}
                  >
                    <textarea
                      {...form.register('invocation_options')}
                      rows={4}
                      placeholder='{"reasoning_effort": "medium"}'
                      className={cn(
                        'w-full rounded-md border bg-background px-3 py-2 font-mono text-[12.5px] leading-relaxed shadow-xs outline-none',
                        'resize-y',
                        'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
                        errors.invocation_options && 'border-destructive ring-destructive/30',
                      )}
                    />
                  </Field>
                </AdvancedGroup>

                <AdvancedGroup heading="Output">
                  <Field
                    label="Branch name"
                    hint="Auto-generated if blank."
                    error={errors.output_branch?.message}
                  >
                    <Input
                      {...form.register('output_branch')}
                      placeholder="harness/nexus-client/2026-04-27"
                      className="font-mono text-[12.5px]"
                      autoComplete="off"
                    />
                  </Field>
                  <Field
                    label="Commit title"
                    hint="Auto-generated if blank."
                    error={errors.output_commit_title?.message}
                  >
                    <Input
                      {...form.register('output_commit_title')}
                      placeholder="Draft kakao clarifying questions"
                      className="font-mono text-[12.5px]"
                      autoComplete="off"
                    />
                  </Field>
                  <Field
                    label="Commit body"
                    hint="Auto-generated if blank."
                    error={errors.output_commit_body?.message}
                  >
                    <textarea
                      {...form.register('output_commit_body')}
                      rows={4}
                      placeholder="Optional commit body in Markdown."
                      className={cn(
                        'w-full rounded-md border bg-background px-3 py-2 font-mono text-[12.5px] leading-relaxed shadow-xs outline-none',
                        'resize-y',
                        'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
                        errors.output_commit_body && 'border-destructive ring-destructive/30',
                      )}
                    />
                  </Field>
                </AdvancedGroup>

                <AdvancedGroup heading="Tracing">
                  <Field
                    label="Metadata"
                    hint="Custom tags for tracking."
                    error={errors.metadata?.message}
                  >
                    <textarea
                      {...form.register('metadata')}
                      rows={4}
                      placeholder='{"nexus_request_id": "req_01HXYZ"}'
                      className={cn(
                        'w-full rounded-md border bg-background px-3 py-2 font-mono text-[12.5px] leading-relaxed shadow-xs outline-none',
                        'resize-y',
                        'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
                        errors.metadata && 'border-destructive ring-destructive/30',
                      )}
                    />
                  </Field>
                  <Field
                    label="Idempotency key"
                    hint="Prevents duplicate runs."
                    error={errors.idempotency_key?.message}
                  >
                    <Input
                      {...form.register('idempotency_key')}
                      placeholder="my-trigger-2026-04-27"
                      className="font-mono text-[12.5px]"
                      autoComplete="off"
                    />
                  </Field>
                </AdvancedGroup>
              </AdvancedSection>
            </div>
          </div>

          <DialogFooter className="border-t border-border/70 px-6 py-3">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="size-3.5 animate-spin" /> : <Wand2 />}
              Trigger run
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function AdvancedSection({
  open,
  onToggle,
  dirty,
  children,
}: {
  open: boolean
  onToggle: () => void
  dirty: boolean
  children: ReactNode
}) {
  return (
    <div className="border-t border-border/60 -mx-6 px-6 pt-4">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="-mx-1 flex w-full items-center gap-2 rounded-md px-1 py-1.5 text-sm font-medium text-foreground/90 hover:text-foreground transition-colors"
      >
        {open ? (
          <ChevronDown className="size-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="size-4 text-muted-foreground" />
        )}
        Advanced settings
        {dirty && !open ? (
          <span className="ml-1 inline-flex size-1.5 rounded-full bg-amber-500" aria-hidden />
        ) : null}
        <span className="ml-auto text-[11px] font-normal text-muted-foreground">
          {open ? 'Hide' : 'Show 9 fields'}
        </span>
      </button>
      {open ? (
        <div className="mt-4 space-y-6 pb-1 animate-in fade-in slide-in-from-top-1">
          {children}
        </div>
      ) : null}
    </div>
  )
}

function AdvancedGroup({
  heading,
  children,
}: {
  heading: string
  children: ReactNode
}) {
  return (
    <div className="space-y-3">
      <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {heading}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  )
}

function Field({
  label,
  hint,
  required,
  error,
  children,
}: {
  label: string
  hint?: string | undefined
  required?: boolean | undefined
  error?: string | undefined
  children: ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <Label className="flex items-center gap-1">
        {label}
        {required ? <span className="text-destructive">*</span> : null}
      </Label>
      {children}
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-[11px] text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  )
}

type TriggerDialogContextValue = {
  openTrigger: (initial?: TriggerInitial) => void
}

const TriggerDialogContext = createContext<TriggerDialogContextValue | null>(null)

export function TriggerDialogProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [initial, setInitial] = useState<TriggerInitial | undefined>(undefined)

  const openTrigger = useCallback((next?: TriggerInitial) => {
    setInitial(next)
    setOpen(true)
  }, [])

  const value = useMemo<TriggerDialogContextValue>(
    () => ({ openTrigger }),
    [openTrigger],
  )

  return (
    <TriggerDialogContext.Provider value={value}>
      {children}
      <TriggerRunDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) setInitial(undefined)
        }}
        initial={initial}
      />
    </TriggerDialogContext.Provider>
  )
}

export function useTriggerDialog(): TriggerDialogContextValue {
  const ctx = useContext(TriggerDialogContext)
  if (!ctx) {
    throw new Error(
      'useTriggerDialog must be used inside <TriggerDialogProvider> (mounted in AppShell).',
    )
  }
  return ctx
}
